import { useEffect, useCallback, type ReactNode } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Stethoscope } from 'lucide-react';
import { useAuth } from '@/context';
import ErrorBoundary from './components/ErrorBoundary';

// Create a client for TanStack Query (used by PACS worklist, etc.)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
});
import AppShell from './components/shell/AppShell';
import { UiPreferencesProvider } from './context/UiPreferencesContext';
import { ensureOwnProviderEntry } from './services/providerDirectoryService';
import { useTheme } from './context/ThemeContextOptimized';
import { PatientContextProvider, usePatientContext } from './context/PatientContext';
import { AppointmentsProvider } from './context/AppointmentsContext';
import { ProblemListProvider } from './context/ProblemListContextOptimized';
import { RiskFactorProvider } from './context/RiskFactorContext';
import { LogoProvider } from './context/LogoContext';
import { TelehealthProvider } from './context/TelehealthContext';
import { InboxProvider } from './context/InboxContext';
import ThemeDebug from './components/ThemeDebug';
import LoggingTestButton from './components/LoggingTestButton';
import { useSessionTimeoutOptimized as useSessionTimeout } from './hooks/useSessionTimeoutOptimized';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import { useMonitorListeners } from './hooks/useCleanupFirestoreListeners';
// summarizerService lazy-loaded on login (not needed at startup)
import DevSeedButton from './components/DevSeedButton';
import './App.css';

export interface ContextProvidersWrapperProps {
  children: ReactNode;
}

// Wrapper component to provide context providers with patient data
const ContextProvidersWrapper = ({ children }: ContextProvidersWrapperProps) => {
  const { patient } = usePatientContext() as { patient: { id?: string; patientId?: string } | null };
  const location = useLocation();
  const routePatientId = location.pathname.match(/^\/patients\/([^/]+)/)?.[1] || '';
  const patientId = routePatientId || patient?.id || patient?.patientId || '';

  return (
    <ProblemListProvider patientId={patientId}>
      <RiskFactorProvider patientId={patientId}>
        {children}
      </RiskFactorProvider>
    </ProblemListProvider>
  );
};

const App = () => {
  const { user, role, isLoading } = useAuth() as {
    user: { uid: string; displayName?: string; email?: string; photoURL?: string } | null;
    role: string | null;
    isLoading: boolean;
  };
  const { darkMode } = useTheme() as { darkMode: boolean };
  const navigate = useNavigate();
  const location = useLocation();

  // Monitor Firestore listeners in development
  useMonitorListeners(30); // Warn if more than 30 listeners

  // Session timeout configuration
  const {
    showTimeoutWarning,
    secondsUntilTimeout,
    isActive,
    extendSession,
    logoutUser
  } = useSessionTimeout({
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    warningMs: 60 * 1000, // 1 minute warning
    redirectPath: '/login',
    logoutMessage: 'You were logged out due to inactivity'
  });

  // Use user's photoURL directly instead of localStorage
  const avatarURL = user?.photoURL || '/default-avatar.png';

  // Store role in localStorage for persistence
  useEffect(() => {
    if (role) {
      localStorage.setItem('userRole', role);
    }
  }, [role]);

  // Providers keep their provider_directory entry current, so front desk and
  // other staff can book them (users isn't listable by non-admins).
  useEffect(() => {
    const u = user as { uid?: string; displayName?: string | null; email?: string | null } | null;
    if (!u?.uid || !role) return;
    ensureOwnProviderEntry({ uid: u.uid, displayName: u.displayName, email: u.email }, role).catch(() => {
      // Non-blocking: the booking form falls back to listing just this provider.
    });
  }, [user, role]);

  // Load AI config from Firestore into localStorage on login (lazy-loaded)
  useEffect(() => {
    if (user) {
      import('./services/summarizerService').then(({ initAIConfig }) => {
        initAIConfig();
      });
    }
  }, [user]);

  // Handle errors in the application
  const handleError = useCallback((error: unknown, _errorInfo: unknown) => {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Application error:', error, _errorInfo);
    }

    // In production, you could send this to a monitoring service like Sentry
    // if (import.meta.env.PROD) {
    //   Sentry.captureException(error);
    // }
  }, []);

  // Navigation handlers for error boundary
  const handleNavigateBack = useCallback(() => {
    navigate(-1); // Go back to previous page
  }, [navigate]);

  const handleNavigateToDashboard = useCallback(() => {
    navigate('/dashboard'); // Go to dashboard
  }, [navigate]);

  // Force dark mode class on body based on current state
  useEffect(() => {
    console.log('App: Applying theme classes in useEffect - darkMode:', darkMode);
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Check if current page requires full width (no padding/container)
  const isFullWidthPage = location.pathname.includes('/pacs') || location.pathname.includes('/telehealth') || location.pathname.includes('/appointments');
  // The patient chart is a dense, multi-pane workspace — max-w-7xl left ~25% of
  // a 1700px clinic monitor as dead gutter. Billing sub-route keeps the default.
  const isChartWorkspace = /^\/patients\/[^/]+\/?$/.test(location.pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <PatientContextProvider>
        <LogoProvider>
          <TelehealthProvider>
            <AppointmentsProvider>
              <InboxProvider>
                <UiPreferencesProvider>
                <ContextProvidersWrapper>
                <div
                  className={`min-h-screen transition-colors duration-150 bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100`}
                >
                  {/* App chrome: top bar (default) or side rail — a per-user preference */}
                  <AppShell user={user} role={role ?? ''} avatarURL={avatarURL}>
                  {/* Portal mount point for modals - under header but above content */}
                  <div id="modal-portal-root" className="relative z-40"></div>

                  {/* Main Content Area */}
                  <main className="relative">
                    {/* Loading State */}
                    {isLoading || !role ? (
                      <div className="min-h-[calc(100vh-var(--app-chrome,3rem))] flex items-center justify-center">
                        <div className="text-center space-y-6 animate-fadeIn">
                          {/* Loading Spinner */}
                          <div className="relative">
                            <div className={`w-16 h-16 rounded-full border-4 border-t-transparent animate-spin border-slate-200 border-t-primary-600 dark:border-slate-600 dark:border-t-primary-500`}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Stethoscope size={24} strokeWidth={1.75} aria-hidden="true" className="text-primary-600 dark:text-primary-400" />
                            </div>
                          </div>

                          {/* Loading Text */}
                          <div className="space-y-2">
                            <h2 className={`text-xl font-semibold text-slate-700 dark:text-slate-200`}>
                              Loading Chartly EMR
                            </h2>
                            <p className={`text-sm text-slate-500 dark:text-slate-400`}>
                              Preparing your healthcare workspace...
                            </p>
                          </div>

                          {/* Loading progress dots */}
                          <div className="flex justify-center space-x-2">
                            {[0, 1, 2].map((i) => (
                              <div
                                key={i}
                                className={`w-2 h-2 rounded-full animate-pulse bg-slate-300 dark:bg-slate-600`}
                                style={{
                                  animationDelay: `${i * 200}ms`,
                                  animationDuration: '1s'
                                }}
                              ></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Application Content */
                      <div className={isFullWidthPage ? "w-full h-full p-0" : isChartWorkspace ? "w-full max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-6 pb-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6"}>
                        <ErrorBoundary
                          onError={handleError}
                          onNavigateBack={handleNavigateBack}
                          onNavigateToDashboard={handleNavigateToDashboard}
                        >
                          <div className="animate-fadeIn">
                            <Outlet context={{ role }} />
                          </div>
                        </ErrorBoundary>
                      </div>
                    )}

                    {/* Development Theme Debug — enable with VITE_SHOW_DEBUG_OVERLAY=true */}
                    {import.meta.env.DEV && import.meta.env.VITE_SHOW_DEBUG_OVERLAY === 'true' && <ThemeDebug />}

                    {/* Logging Test Button — enable with VITE_SHOW_DEBUG_OVERLAY=true */}
                    {import.meta.env.DEV && import.meta.env.VITE_SHOW_DEBUG_OVERLAY === 'true' && <LoggingTestButton />}

                    {/* Dev Seed Button — emulator mode only */}
                    {import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true' && <DevSeedButton />}

                    {/* Session Timeout Warning Modal */}
                    {showTimeoutWarning && (
                      <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity animate-fadeIn"></div>
                          <div className="relative transform overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-2xl transition-colors duration-100 animate-scaleIn">
                            <SessionTimeoutWarning
                              secondsUntilTimeout={secondsUntilTimeout}
                              isActive={isActive}
                              onContinue={extendSession}
                              onLogout={logoutUser}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </main>
                  </AppShell>

                  {/* Background Pattern - Subtle healthcare-themed background */}
                  <div className={`fixed inset-0 -z-10 opacity-5 opacity-[0.03] dark:opacity-[0.02]`}>
                    <svg width="60" height="60" viewBox="0 0 60 60" className="absolute inset-0 h-full w-full">
                      <defs>
                        <pattern id="healthcare-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                          <path d="M30 15v30m-15-15h30" stroke="currentColor" strokeWidth="1" />
                          <circle cx="30" cy="30" r="3" fill="currentColor" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#healthcare-pattern)" />
                    </svg>
                  </div>
                </div>
              </ContextProvidersWrapper>
                </UiPreferencesProvider>
              </InboxProvider>
            </AppointmentsProvider>
          </TelehealthProvider>
        </LogoProvider>
      </PatientContextProvider>
    </QueryClientProvider>
  );
};

export default App;
